import React from 'react';
import styled from 'styled-components';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import { fonts } from '../gatsby-theme-material-ui-top-layout/theme';

import Button from './Button';
import LinkWrapper from './LinkWrapper';
import TypewriterText from './TypewriterText';

import PropyLogo from '../img/propy-light-mode.svg';

import SocialIconX from '../img/social-icon-x.svg';
import SocialIconInstagram from '../img/social-icon-instagram.svg';
import SocialIconTiktok from '../img/social-icon-tiktok.svg';
import SocialIconFacebook from '../img/social-icon-facebook.svg';
import SocialIconLinkedIn from '../img/social-icon-linkedin.svg';
import SocialIconYoutube from '../img/social-icon-youtube.svg';

import {
  PROPY_LIGHTER_GREY,
  PROPY_SPACE_GREY_LIGHT,
  ONBOARDING_ROOT,
} from '../utils';

const socials = [
  {
    icon: SocialIconX,
    link: "https://x.com/PropyInc",
  },
  {
    icon: SocialIconInstagram,
    link: "https://www.instagram.com/propy.inc/",
  },
  {
    icon: SocialIconTiktok,
    link: "https://www.tiktok.com/@propy_inc",
  },
  {
    icon: SocialIconFacebook,
    link: "https://www.facebook.com/propyinc",
  },
  {
    icon: SocialIconLinkedIn,
    link: "https://www.linkedin.com/company/propy-title",
  },
  {
    icon: SocialIconYoutube,
    link: "https://www.youtube.com/@Propy",
  },
]

const filteredSocials = socials.filter((socialEntry) => socialEntry.link);

const SocialsContainer = styled.div`
  display: flex;
  align-items: center;
  ${props => props.mobile ? `
    margin-bottom: ${props.theme.spacing(2)};
    justify-content: center;
  ` : props.medium ? `
    margin-top: ${props.theme.spacing(4)};
    margin-bottom: ${props.theme.spacing(2)};
    justify-content: center;
  ` : `
    margin-top: ${props.theme.spacing(2)};
    justify-content: end;
  `};
`

const ZoneContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const ZoneInnerContainer = styled.div`
  max-width: 1080px;
  width: 100%;
`

const TitleContainer = styled.div`
  text-align: ${props => props.mobile ? 'center' : 'left'};
  margin-bottom: ${props => props.theme.spacing(12)};
  max-width: ${props => props.mobile ? '300px' : '980px'};
  margin-left: ${props => props.mobile ? 'auto' : '0px'};
  margin-right: ${props => props.mobile ? 'auto' : '0px'};
`

const PropyLogoBox = styled(Box)`
  width: 70px;
  height: 16px;
  margin-top: 14px;
  margin-bottom: 14px;
`

const FooterColumnHeaderTypography = styled(Typography)`
  font-family: ${fonts.spaceGrotesk}!important;
  font-weight: 400!important;
  color: ${PROPY_SPACE_GREY_LIGHT}!important;
  font-size: 14px!important;
  letter-spacing: 0.12em!important;
  line-height: 19px!important;
  margin-bottom: ${props => props.theme.spacing(2)}!important;
`

const FooterLinkTypography = styled(Typography)`
  font-family: ${fonts.spaceGrotesk}!important;
  font-weight: 400!important;
  color: ${PROPY_LIGHTER_GREY}!important;
  font-size: 14px!important;
  letter-spacing: -0.01em!important;
  line-height: 19px!important;
  &:hover {
    color: ${PROPY_SPACE_GREY_LIGHT}!important;
  }
`

const SocialsZone = ({
  isConsideredMobile,
  isConsideredMedium,
}) => {
  return (
    <SocialsContainer mobile={isConsideredMobile} medium={isConsideredMedium}>
      {filteredSocials.map((socialEntry, index) => 
        <LinkWrapper link={socialEntry.link} key={`social-${socialEntry.link}-${index}`} external={true}>
          <Box
            component={socialEntry.icon}
            sx={{
              ...(index < (filteredSocials.length - 1) && {
                marginRight: '16px',
              }),
              '& path': { fill: '#696b84' }
            }}
          />
        </LinkWrapper>
      )}
    </SocialsContainer>
  )
}

const LogoZone = ({isConsideredMedium, isConsideredMobile}) => {

  return (
    <div>
      {(isConsideredMobile || isConsideredMedium) &&
        <SocialsZone isConsideredMobile={isConsideredMobile} isConsideredMedium={isConsideredMedium}/>
      }
      <div style={{display: 'flex', justifyContent: (isConsideredMobile || isConsideredMedium) ? 'center' : 'end'}}>
        <PropyLogoBox component={PropyLogo} />
      </div>
      <FooterLinkTypography variant="body1">
        © 2025 Propy, Inc. All rights reserved
      </FooterLinkTypography>
      <div>
        <LinkWrapper style={{justifyContent: (isConsideredMobile || isConsideredMedium) ? 'center' : 'end'}} hoverUnderline external={true} link="https://propy.com/browse/terms-and-conditions/">
          <FooterLinkTypography variant="body1">
            Terms & Conditions
          </FooterLinkTypography>
        </LinkWrapper>
        <LinkWrapper style={{justifyContent: (isConsideredMobile || isConsideredMedium) ? 'center' : 'end'}} hoverUnderline external={true} link="https://propy.com/browse/privacy-policy/">
          <FooterLinkTypography variant="body1">
            Privacy
          </FooterLinkTypography>
        </LinkWrapper>
        <LinkWrapper style={{justifyContent: (isConsideredMobile || isConsideredMedium) ? 'center' : 'end'}} hoverUnderline external={true} link="https://propy.com/browse/cookies-policy/">
          <FooterLinkTypography variant="body1">
            Cookies
          </FooterLinkTypography>
        </LinkWrapper>
      </div>
      {(!isConsideredMobile && !isConsideredMedium) &&
        <SocialsZone isConsideredMobile={isConsideredMobile} isConsideredMedium={isConsideredMedium} />
      }
    </div>
  )
}

const Footer = ({ 
  isConsideredMobile,
  isConsideredMedium,
}) => {
  return (
    <ZoneContainer>
      <ZoneInnerContainer>
        <TitleContainer mobile={isConsideredMobile}>
          <Typography sx={{ fontFamily: fonts.spaceGrotesk, color: PROPY_SPACE_GREY_LIGHT, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '16px', textAlign: isConsideredMobile ? 'center' : 'left', ...(isConsideredMobile && { fontSize: '14px', lineHeight: '14px' }) }}>Don’t get left behind</Typography>
          <TypewriterText 
            text="Call us to discuss your specific needs and discover how our title and escrow services can enhance your real estate transactions."
            speed={25} // slower typing
            isConsideredMobile={isConsideredMobile}
          />
          <LinkWrapper link={`${ONBOARDING_ROOT}/get-started/agent`} external={true} noFlex={true}>
            <Button 
              marginTop={'32px'}
              text="Book a demo"
            />
          </LinkWrapper>
        </TitleContainer>
          <Grid container spacing={2} style={{height: '100%', alignItems: 'start', justifyContent: 'center'}}>
            {(!isConsideredMobile) &&
              <Grid item xs={12} sm={12} md={12} lg={8}>
                <Grid container columns={20} spacing={2}>
                  <Grid item xs={20} sm={20} md={4} lg={4}>
                    <FooterColumnHeaderTypography variant="body1">
                      ONBOARDING
                    </FooterColumnHeaderTypography>
                    <LinkWrapper hoverUnderline link={`${ONBOARDING_ROOT}/get-started`} external={true}>
                      <FooterLinkTypography variant="body1">
                        Get Started
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline link={`${ONBOARDING_ROOT}/get-started/agent`}>
                      <FooterLinkTypography variant="body1">
                        I'm an Agent
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline link={`${ONBOARDING_ROOT}/get-started/buyer`}>
                      <FooterLinkTypography variant="body1">
                        I'm a Buyer
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline link={`${ONBOARDING_ROOT}/get-started/seller`}>
                      <FooterLinkTypography variant="body1">
                        I'm a Seller
                      </FooterLinkTypography>
                    </LinkWrapper>
                  </Grid>
                  <Grid item xs={20} sm={20} md={4} lg={4}>
                    <FooterColumnHeaderTypography variant="body1">
                      OFFERINGS
                    </FooterColumnHeaderTypography>
                    <LinkWrapper hoverUnderline external={true} link={`https://propy.com/browse/subscriptions/`}>
                      <FooterLinkTypography variant="body1">
                        Pricing
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://propy.com/browse/crypto-for-real-estate/`}>
                      <FooterLinkTypography variant="body1">
                        Courses
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://propy.com/browse/resources/`}>
                      <FooterLinkTypography variant="body1">
                        Resources
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://propy.com/browse/about/`}>
                      <FooterLinkTypography variant="body1">
                        About Propy
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://coinmarketcap.com/currencies/propy/`}>
                      <FooterLinkTypography variant="body1">
                        Propy Token
                      </FooterLinkTypography>
                    </LinkWrapper>
                  </Grid>
                  <Grid item xs={20} sm={20} md={4} lg={4}>
                    <FooterColumnHeaderTypography variant="body1">
                      EDUCATION
                    </FooterColumnHeaderTypography>
                    <LinkWrapper hoverUnderline external={true} link={`https://propy.com/browse/knowledge-base/`}>
                      <FooterLinkTypography variant="body1">
                        Guides
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://propy.com/browse/webinars/`}>
                      <FooterLinkTypography variant="body1">
                        Webinars
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://propy.com/browse/case-studies/`}>
                      <FooterLinkTypography variant="body1">
                        Case Studies
                      </FooterLinkTypography>
                    </LinkWrapper>
                  </Grid>
                  <Grid item xs={20} sm={20} md={4} lg={4}>
                    <FooterColumnHeaderTypography variant="body1">
                      PRESS
                    </FooterColumnHeaderTypography>
                    <LinkWrapper hoverUnderline external={true} link={`https://propy.com/browse/blog/`}>
                      <FooterLinkTypography variant="body1">
                        Blog
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://propy.com/browse/careers/`}>
                      <FooterLinkTypography variant="body1">
                        Careers
                      </FooterLinkTypography>
                    </LinkWrapper>
                  </Grid>
                  <Grid item xs={20} sm={20} md={4} lg={4}>
                    <FooterColumnHeaderTypography variant="body1">
                      SOCIAL
                    </FooterColumnHeaderTypography>
                    <LinkWrapper hoverUnderline external={true} link={`https://www.facebook.com/propyinc`}>
                      <FooterLinkTypography variant="body1">
                        Facebook
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://www.instagram.com/propy.inc/`}>
                      <FooterLinkTypography variant="body1">
                        Instagram
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://twitter.com/PropyInc`}>
                      <FooterLinkTypography variant="body1">
                        Twitter
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://discord.gg/7pjUugx97A`}>
                      <FooterLinkTypography variant="body1">
                        Discord
                      </FooterLinkTypography>
                    </LinkWrapper>
                    <LinkWrapper hoverUnderline external={true} link={`https://t.me/propy`}>
                      <FooterLinkTypography variant="body1">
                        Telegram
                      </FooterLinkTypography>
                    </LinkWrapper>
                  </Grid>
                </Grid>
              </Grid>
            }
            {(!isConsideredMedium && !isConsideredMobile) &&
              <Grid item xs={12} sm={12} md={12} lg={4} style={{height: '100%', paddingTop: 0, textAlign: 'end', display: 'flex', justifyContent: 'end'}}>
                <LogoZone isConsideredMobile={isConsideredMobile} isConsideredMedium={isConsideredMedium} />
              </Grid>
            }
            {(isConsideredMedium || isConsideredMobile) &&
              <Grid item xs={12} sm={12} md={12} lg={6} style={{height: '100%', paddingTop: 0, display: 'flex', justifyContent: 'center'}}>
                <LogoZone isConsideredMobile={isConsideredMobile} isConsideredMedium={isConsideredMedium} />
              </Grid>
            }
          </Grid>
        </ZoneInnerContainer>
    </ZoneContainer>
  );
};

export default Footer;